import { useLayoutEffect, useRef } from 'react';

/** Zwei Zeilen bleiben immer stehen — ein einzeiliges Feld sieht nicht aus wie eine Notiz. */
const MIN_HOEHE = 56;

/**
 * Notizfeld, das mit dem Text mitwächst.
 *
 * Eine Notiz ist meistens ein Satz, manchmal ein Telefonprotokoll. Ein fester Rahmen zwingt
 * bei Letzterem zum Scrollen in einem Feld von zwei Zeilen Höhe — man sieht nie, was man
 * geschrieben hat. Deshalb wächst die Höhe mit dem Inhalt statt einen Rollbalken zu zeigen.
 *
 * Unkontrolliert (defaultValue + onBlur) wie zuvor: Beim Tippen soll nichts ans Netz gehen,
 * gespeichert wird beim Verlassen des Feldes.
 */
export default function NotizFeld({
  wert,
  placeholder,
  onSpeichern,
}: {
  wert: string | null;
  placeholder: string;
  onSpeichern: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const anpassen = () => {
    const el = ref.current;
    if (!el) return;
    // Erst zurücksetzen: sonst kennt scrollHeight nur die bisherige (größere) Höhe und das
    // Feld könnte beim Löschen von Text nie wieder schrumpfen.
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, MIN_HOEHE)}px`;
  };

  // Layout-Effekt statt useEffect: Die Höhe steht schon vor dem ersten Bild, sonst zuckt die
  // Karte beim Aufklappen sichtbar.
  useLayoutEffect(anpassen, [wert]);

  return (
    <textarea
      ref={ref}
      rows={2}
      defaultValue={wert ?? ''}
      onInput={anpassen}
      onBlur={(e) => { if (e.target.value !== (wert ?? '')) onSpeichern(e.target.value); }}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none overflow-hidden"
    />
  );
}
